import { createSlice } from '@reduxjs/toolkit';
import { authApi } from '../auth/authApi'
import { IChatboxChat, IChatResponse } from '../../interfaces/chats/IChatResponse';
import { chatboxApi } from '../chatbox/chatboxApi';
import Cookies from 'js-cookie';
import { DOMAIN } from '../../config';


const initialState: {
	isLoggedIn: boolean;
	isAdmin: boolean;
    topicId: string
    chatTitle: string,
    messages: IChatboxChat[],
	authMessages: IChatResponse[],
    isSending: boolean,
    error?: string | null
	showChatInput: boolean,
	clarifyMode?: boolean
} = {
	isLoggedIn: false,
	isAdmin: false,
    topicId: '',
    chatTitle: '',
    messages: [],  
	authMessages: [],
    isSending: false, 
    error: null, 
	showChatInput: true,
	clarifyMode: false
};

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        setChatId: (state, action) => {
            state.topicId = action.payload;
        },
		setAuthChats: (state, action) => {
            state.authMessages = action.payload;
        },
		setChatMessages: (state, action) => {
			state.messages = action.payload
		},
		showChatInput: (state, action) => {
			state.showChatInput = action.payload
		},
		setClarifyMessage: (state, action) => {
			state.messages = state.messages.map(message => message._id === action.payload ? {...message, clarify: true} : message)
			state.clarifyMode = true;
			state.showChatInput = true
		}
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                authApi.endpoints.authWithAgent.matchPending,
                (state, action) => {
					state.authMessages = action.meta.arg.originalArgs.chatData.map(
						(arg: IChatResponse, index: number) => 
							index === action.meta.arg.originalArgs.chatData.length - 1 ? 
							{
								user: {
									...arg.user,
									requestId: action.meta.requestId,
									type: 'pending'
								}
							}
							: arg );
                    state.isSending = true;
                }
            )
            .addMatcher(
                authApi.endpoints.authWithAgent.matchRejected,
                (state, action) => {
                    state.isSending = false;
					state.authMessages = state.authMessages.map(
						(arg: IChatResponse) => 
							arg.user?.requestId === action.meta.requestId ? 
							{
								user: {
									...arg.user,
									type: 'error'
								}
							}
							: arg );
							const errorObj = action as {payload: {data: {message: string}}}; 
                    		state.error = errorObj.payload.data.message || action.error.message;
                }
            )
            .addMatcher(
                authApi.endpoints.authWithAgent.matchFulfilled,
                (state, action) => {
                    state.isSending = false;
					if (action.payload.data.metadata) {
						const {accessToken, refreshToken, chatId, isAdmin} = action.payload.data.metadata;
						if (accessToken) {
							state.isLoggedIn = true
							localStorage.setItem('accessToken', accessToken);
							Cookies.set('accessToken', accessToken, { 
								domain: DOMAIN,
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
						if (refreshToken) {
							localStorage.setItem('refreshToken', refreshToken);
							Cookies.set('refreshToken', refreshToken, { 
								domain: DOMAIN,
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
						if (chatId) {
							state.topicId = chatId;
						}
						if (isAdmin){
							state.isAdmin = true
							Cookies.set('roles', 'admins', { 
								domain: DOMAIN,
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
					}
					state.authMessages = [...action.payload.data.chatData]
                }
            )
			
			.addMatcher(
                authApi.endpoints.signInWithAgent.matchPending,
                (state, action) => {
					state.authMessages = action.meta.arg.originalArgs.chatData.map(
						(arg: IChatResponse, index: number) => 
							index === action.meta.arg.originalArgs.chatData.length - 1 ? 
							{
								user: {
									...arg.user,
									requestId: action.meta.requestId,
									type: 'pending',
								}
							}
							: arg );
                    state.isSending = true;
                }
            )
            .addMatcher(
                authApi.endpoints.signInWithAgent.matchRejected,
                (state, action) => {
                    state.isSending = false;
					state.authMessages = state.authMessages.map(
						(arg: IChatResponse) => 
							arg.user?.requestId === action.meta.requestId ? 
							{
								user: {
									...arg.user,
									type: 'error'
								}
							}
							: arg );
							const errorObj = action as {payload: {data: {message: string}}}; 
                    		state.error = errorObj.payload.data.message || action.error.message;
                }
            )
            .addMatcher(
                authApi.endpoints.signInWithAgent.matchFulfilled,
                (state, action) => {
                    state.isSending = false;
					if (action.payload.data.metadata) {
						const {accessToken, refreshToken, chatId, isAdmin} = action.payload.data.metadata;
						if (accessToken) {
							state.isLoggedIn = true;
							localStorage.setItem('accessToken', accessToken);
							Cookies.set('accessToken', accessToken, { 
								domain: DOMAIN,
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
						if (refreshToken) {
							localStorage.setItem('refreshToken', refreshToken);
							Cookies.set('refreshToken', refreshToken, { 
								domain: DOMAIN,
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
						if (chatId) {
							state.topicId = chatId;
						}
						if (isAdmin){
							state.isAdmin = true
							Cookies.set('roles', 'admins', { 
								domain: DOMAIN,
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
					}
					state.authMessages = [...action.payload.data.chatData]
                }
            )

			.addMatcher(
                chatboxApi.endpoints.chatbox.matchPending,
                (state, action) => {
					state.messages.push({ 
						_id: action.meta.requestId,
						message: action.meta.arg.originalArgs.message, 
						files: action.meta.arg.originalArgs.files,
						requestId: action.meta.requestId, 
						type: 'pending', 
						msgType: action.meta.arg.originalArgs.files?.length ? 'file' : 'message',
						updatedAt: new Date().toUTCString(),
						createdAt: new Date().toUTCString(),
						userId: '', 
						topic: '',
						user: {
							name: 'my name'
						},
						clarify: action.meta.arg.originalArgs.clarify
					},
					{ 
						_id: action.meta.requestId,
						message: '', 
						requestId: action.meta.requestId, 
						type: 'pending', 
						msgType: 'message',
						updatedAt: new Date().toUTCString(),
						createdAt: new Date().toUTCString(),
						userId: '', 
						topic: '',
						agent: {
							name: 'my name'
						}
					});
                    state.isSending = true;
                }
            )
            .addMatcher(
                chatboxApi.endpoints.chatbox.matchRejected,
                (state, action) => {
                    state.isSending = false;
					state.messages = state.messages.filter(message => !(message.requestId === action.meta.requestId && message.agent)).map(
						(arg: IChatboxChat) => 
							arg.requestId === action.meta.requestId ? 
							{
								...arg,
								type: 'error'
							}
							: arg );
                    state.error = action.error.message;
                }
            )
            .addMatcher(
                chatboxApi.endpoints.chatbox.matchFulfilled,
                (state, action) => {
                    state.isSending = false;
					const rawChats = action.payload.data;
					const chats = Array.isArray(rawChats) ? rawChats : rawChats ? [rawChats] : [];
					const agentChat = [...chats].reverse().find(c => c.agent) || chats[chats.length - 1];
					const updated = state.messages.map(
						(arg: IChatboxChat) => 
							arg.requestId === action.meta.requestId ? 
							arg.user ? {
								...arg,
								type: 'success'
							} : {...arg, ...agentChat, type: 'success', instantReply: true, _id: agentChat?._id || ''}
							: arg );
					state.topicId = action.payload.data.topic;
					state.messages = [...updated]
					state.showChatInput = false;
                }
            )
            
    },
});

export const { setChatId, setAuthChats, setChatMessages, setClarifyMessage, showChatInput } = chatSlice.actions;
export default chatSlice.reducer;